import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "../../api";
import { useCurrentUser } from "../../auth";
import {
  PRINTER_DELETE_PERMISSION,
  PRINTER_REGISTRATION_CODE_CREATE_PERMISSION,
} from "../../constants/permissions";
import {
  Alert,
  Button,
  ConfirmDialog,
  Empty,
  Field,
  Input,
  Loading,
  Status,
} from "../../components/ui";
import {
  dateLabel,
  Feedback,
  LoadError,
  printerIsOnline,
  useAction,
  useResource,
  type Printer,
} from "./shared";

export default function PrinterSettings() {
  const { t, i18n } = useTranslation("settings");
  const api = useApi();
  const { permissions } = useCurrentUser();
  const supportEmail = import.meta.env.VITE_APP_ADMIN_EMAIL as
    | string
    | undefined;
  const remote = useResource<Printer[]>("/printers");
  const action = useAction();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [expiry, setExpiry] = useState(60);
  const [code, setCode] = useState<{ code: string; expires_at: string } | null>(
    null,
  );
  const [remove, setRemove] = useState<Printer | null>(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 10000);
    return () => clearInterval(timer);
  }, []);
  const expired = code ? new Date(code.expires_at).getTime() <= now : false;
  const generate = (event: FormEvent) => {
    event.preventDefault();
    void action.run(async () => {
      const response = await api.post("/printers/registration-codes", {
        label: name.trim(),
        expires_minutes: expiry,
      });
      setCode(response);
    }, t("printers.codeCreated"));
  };
  return (
    <section className="settings-section">
      <div className="settings-section-heading">
        <h2>{t("sections.printers")}</h2>
        <div className="settings-inline">
          <Button
            variant="ghost"
            disabled={remote.loading}
            onClick={() => void remote.reload()}
          >
            {t("common.refresh")}
          </Button>
          {permissions.includes(
            PRINTER_REGISTRATION_CODE_CREATE_PERMISSION,
          ) && (
            <Button
              variant={adding ? "outline" : "default"}
              onClick={() => {
                setAdding(!adding);
                setCode(null);
              }}
            >
              {t("printers.connect")}
            </Button>
          )}
        </div>
      </div>
      <LoadError {...remote} />
      <Feedback {...action} />
      {adding && (
        <form
          className="settings-form settings-inline-form settings-form-width"
          onSubmit={generate}
        >
          <h3>{t("printers.connect")}</h3>
          <p className="settings-muted">{t("printers.connectHint")}</p>
          <p className="settings-muted">
            {t("printers.appHelp")}
            {supportEmail && (
              <>
                {" "}
                <a href={`mailto:${supportEmail}`}>
                  {t("printers.requestApp")}
                </a>
              </>
            )}
          </p>
          <Field htmlFor="printer-name" label={t("printers.name")}>
            <Input
              id="printer-name"
              value={name}
              maxLength={80}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={action.busy}
              autoFocus
            />
          </Field>
          <Field htmlFor="printer-expiry" label={t("printers.expiry")}>
            <Input
              id="printer-expiry"
              type="number"
              min="5"
              max="10080"
              step="1"
              value={expiry}
              onChange={(e) => setExpiry(Number(e.target.value))}
              required
              disabled={action.busy}
            />
          </Field>
          <div className="settings-inline">
            <Button
              type="submit"
              disabled={
                action.busy || !name.trim() || expiry < 5 || expiry > 10080
              }
            >
              {t("printers.generate")}
            </Button>
            <Button
              variant="ghost"
              disabled={action.busy}
              onClick={() => {
                setAdding(false);
                setCode(null);
              }}
            >
              {t("common.close")}
            </Button>
          </div>
          {code && (
            <div className="settings-secret">
              {expired ? (
                <Alert variant="warning">{t("printers.expired")}</Alert>
              ) : (
                <>
                  <Field
                    label={t("printers.code")}
                    htmlFor="printer-code"
                    hint={t("printers.expiresAt", {
                      date: dateLabel(code.expires_at, i18n.language),
                    })}
                  >
                    <Input
                      id="printer-code"
                      readOnly
                      value={code.code}
                      autoComplete="off"
                    />
                  </Field>
                  <Button
                    variant="outline"
                    onClick={() =>
                      void action.run(
                        () => navigator.clipboard.writeText(code.code),
                        t("common.copied"),
                      )
                    }
                  >
                    {t("common.copy")}
                  </Button>
                </>
              )}
            </div>
          )}
        </form>
      )}
      {remote.loading && !remote.data ? (
        <Loading label={t("common.loading")} />
      ) : remote.data?.length ? (
        <div className="settings-table-wrap">
          <table className="settings-table">
            <caption className="sr-only">{t("printers.connected")}</caption>
            <thead>
              <tr>
                <th>{t("printers.name")}</th>
                <th>{t("printers.state")}</th>
                <th>{t("printers.lastSeen")}</th>
                <th>{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {remote.data.map((printer) => (
                <tr key={printer.id}>
                  <td data-label={t("printers.name")}>
                    <strong>{printer.display_name}</strong>
                    {printer.queue_name && (
                      <span className="settings-muted settings-block">
                        {printer.queue_name}
                      </span>
                    )}
                  </td>
                  <td data-label={t("printers.state")}>
                    <Status
                      tone={
                        printerIsOnline(printer, now) ? "success" : "warning"
                      }
                    >
                      {t(
                        printerIsOnline(printer, now)
                          ? "printers.online"
                          : "printers.offline",
                      )}
                    </Status>
                  </td>
                  <td data-label={t("printers.lastSeen")}>
                    {dateLabel(printer.last_seen_at, i18n.language)}
                  </td>
                  <td data-label={t("common.actions")}>
                    {permissions.includes(PRINTER_DELETE_PERMISSION) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={t("printers.removeNamed", {
                          name: printer.display_name,
                        })}
                        disabled={action.busy}
                        onClick={() => setRemove(printer)}
                      >
                        {t("common.remove")}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        !remote.error && (
          <Empty
            title={t("printers.empty")}
            description={t("printers.emptyHint")}
          />
        )
      )}
      <p className="settings-muted">{t("printers.freshness")}</p>
      <ConfirmDialog
        open={Boolean(remove)}
        onOpenChange={(open) => !open && setRemove(null)}
        title={t("printers.remove")}
        description={t("printers.removeWarning", {
          name: remove?.display_name,
        })}
        confirmLabel={t("printers.remove")}
        cancelLabel={t("common.cancel")}
        pending={action.busy}
        onConfirm={async () => {
          if (!remove) return;
          await action.run(async () => {
            await api.del(`/printers/${remove.id}`);
            setRemove(null);
            await remote.reload();
          }, t("printers.removed"));
        }}
      />
    </section>
  );
}
